import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Plus, FileDown, LockKeyhole, Settings2, BookLock, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useVault } from "@/context/VaultContext";
import { wrapVaultKey, KDF_ITERATIONS } from "@/lib/vaultCrypto";
import { VAULT_CATEGORIES } from "@/lib/vaultMeta";
import VaultSetup from "@/components/vault/VaultSetup";
import VaultUnlock from "@/components/vault/VaultUnlock";
import VaultEntryForm from "@/components/vault/VaultEntryForm";
import VaultEntryCard from "@/components/vault/VaultEntryCard";
import VaultHelp from "@/components/vault/VaultHelp";
import VaultReminderToggle from "@/components/vault/VaultReminderToggle";
import { toast } from "sonner";

function VaultSettings({ open, onOpenChange }) {
  const { vaultKey, lock } = useVault();
  const [np, setNp] = useState(""); const [nc, setNc] = useState(""); const [del, setDel] = useState(""); const [busy, setBusy] = useState(false);

  const changePass = async (e) => {
    e.preventDefault();
    if (np.length < 10) return toast.error("At least 10 characters");
    if (np !== nc) return toast.error("Passphrases don't match");
    setBusy(true);
    try {
      const p = await wrapVaultKey(vaultKey, np);
      await api.put("/vault/keys", { kdf_salt: p.salt, kdf_iterations: KDF_ITERATIONS, wrapped_key: p.wrapped, wrapped_key_iv: p.iv });
      toast.success("Passphrase changed"); setNp(""); setNc(""); onOpenChange(false);
    } finally { setBusy(false); }
  };

  const deleteVault = async () => {
    setBusy(true);
    try {
      await api.delete("/vault", { data: { confirm: del } });
      toast.success("Vault deleted"); onOpenChange(false); lock(); window.location.reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="vault-settings">
        <DialogHeader><DialogTitle className="font-display text-2xl" style={{ fontWeight: 800 }}>Vault settings</DialogTitle><DialogDescription>Change your passphrase or delete the vault. Auto-locks after 15 minutes idle.</DialogDescription></DialogHeader>
        <form onSubmit={changePass} className="space-y-3">
          <Label>New passphrase</Label>
          <Input data-testid="vault-settings-newpass" type="password" value={np} onChange={(e) => setNp(e.target.value)} />
          <Input data-testid="vault-settings-confirm" type="password" placeholder="Confirm" value={nc} onChange={(e) => setNc(e.target.value)} />
          <Button data-testid="vault-settings-save" type="submit" disabled={!np || busy} className="w-full rounded-full bg-slate-900 hover:bg-emerald-600">Change passphrase</Button>
        </form>
        <div className="border-t border-slate-200 pt-4 mt-2 space-y-2">
          <Label className="text-rose-700">Delete vault permanently</Label>
          <p className="text-xs text-slate-500">Removes every entry and attachment. Export first if you may need them. Type <b>DELETE</b> to confirm.</p>
          <Input data-testid="vault-delete-confirm-input" value={del} onChange={(e) => setDel(e.target.value)} placeholder="DELETE" />
          <Button data-testid="vault-delete-btn" type="button" variant="outline" disabled={del !== "DELETE" || busy} onClick={deleteVault} className="w-full rounded-full border-rose-300 text-rose-700 hover:bg-rose-50">Delete vault</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const { entries, lock } = useVault();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [settings, setSettings] = useState(false);
  const [cat, setCat] = useState("");
  const [sup, setSup] = useState("");

  const supervisors = useMemo(() => {
    const m = new Map();
    entries.forEach((e) => e.data?.supervisor_id && m.set(e.data.supervisor_id, { name: e.data.supervisor_name, n: (m.get(e.data.supervisor_id)?.n || 0) + 1 }));
    return [...m.entries()];
  }, [entries]);

  const visible = entries.filter((e) => (!cat || e.data?.category === cat) && (!sup || e.data?.supervisor_id === sup));

  return (
    <div data-testid="vault-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-emerald-600 mb-1 flex items-center gap-1.5"><Lock size={13} /> Unlocked · end-to-end encrypted</p>
          <h1 className="font-display text-4xl sm:text-5xl text-slate-900" style={{ fontWeight: 900 }}>Documentation Vault</h1>
          <p className="text-slate-500 mt-1">{entries.length} {entries.length === 1 ? "entry" : "entries"}. Only you can read this.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="vault-new-entry-btn" onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-slate-900 hover:bg-emerald-600 rounded-full gap-2"><Plus size={16} /> New entry</Button>
          <Button data-testid="vault-export-btn" variant="outline" onClick={() => navigate("/vault/export")} disabled={entries.length === 0} className="rounded-full gap-2"><FileDown size={16} /> Export</Button>
          <Button data-testid="vault-settings-btn" variant="ghost" size="icon" onClick={() => setSettings(true)} title="Settings"><Settings2 size={18} /></Button>
          <Button data-testid="vault-lock-btn" variant="ghost" size="icon" onClick={lock} title="Lock vault"><LockKeyhole size={18} /></Button>
        </div>
      </div>

      <VaultReminderToggle />

      {entries.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-5 text-sm" data-testid="vault-filters">
          <Filter size={14} className="text-slate-400" />
          <button data-testid="vault-filter-all" onClick={() => { setCat(""); setSup(""); }} className={`px-3 py-1 rounded-full border ${!cat && !sup ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600"}`}>All</button>
          {VAULT_CATEGORIES.filter((c) => entries.some((e) => e.data?.category === c.key)).map((c) => (
            <button key={c.key} data-testid={`vault-filter-${c.key}`} onClick={() => setCat(cat === c.key ? "" : c.key)} className={`px-3 py-1 rounded-full border ${cat === c.key ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600"}`}>{c.label}</button>
          ))}
          {supervisors.map(([id, s]) => (
            <button key={id} data-testid={`vault-filter-supervisor-${id}`} onClick={() => setSup(sup === id ? "" : id)} className={`px-3 py-1 rounded-full border ${sup === id ? "bg-emerald-600 text-white border-emerald-600" : "border-emerald-300 text-emerald-700"}`}>{s.name?.split(" · ")[0]} · {s.n}</button>
          ))}
        </div>
      )}

      {entries.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center" data-testid="vault-empty">
          <BookLock size={40} className="mx-auto text-slate-300 mb-3" />
          <h2 className="font-display text-2xl text-slate-900" style={{ fontWeight: 700 }}>Your vault is empty</h2>
          <p className="text-slate-500 mt-2 max-w-md mx-auto">Record incidents as soon as they happen: date, exact words, who was there. Contemporaneous notes carry far more weight than memories reconstructed months later.</p>
          <Button onClick={() => setFormOpen(true)} className="mt-5 bg-slate-900 hover:bg-emerald-600 rounded-full gap-2"><Plus size={16} /> Write your first entry</Button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="vault-entries">
          {visible.map((e) => <VaultEntryCard key={e.id} entry={e} onEdit={(en) => { setEditing(en); setFormOpen(true); }} />)}
          {visible.length === 0 && <p className="text-slate-400 text-sm">No entries match this filter.</p>}
        </div>
      )}

      <VaultEntryForm open={formOpen} onOpenChange={setFormOpen} existing={editing} />
      <VaultSettings open={settings} onOpenChange={setSettings} />
    </div>
  );
}

export default function Vault() {
  const { user, setAuthOpen } = useAuth();
  const { unlocked, unlock } = useVault();
  const [vault, setVault] = useState(null);

  useEffect(() => {
    if (!user) { setVault(null); return; }
    api.get("/vault").then((r) => setVault(r.data));
  }, [user, unlocked]);

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-5 py-20 text-center" data-testid="vault-signin-prompt">
        <BookLock size={44} className="mx-auto text-slate-300 mb-4" />
        <h1 className="font-display text-4xl text-slate-900" style={{ fontWeight: 900 }}>Documentation Vault</h1>
        <p className="text-slate-500 mt-3 leading-relaxed">A private, end-to-end encrypted diary for documenting harassment, discrimination, or retaliation — so you have a dated record if you ever need one. Free for every employee.</p>
        <Button data-testid="vault-signin-btn" onClick={() => setAuthOpen(true)} className="mt-6 bg-slate-900 hover:bg-emerald-600 rounded-full px-7">Sign in to open your vault</Button>
        <div className="text-left"><VaultHelp /></div>
      </div>
    );
  }

  if (!vault) return <div className="max-w-5xl mx-auto px-5 py-16 text-slate-400 font-mono text-sm">Loading...</div>;

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
      {!vault.exists ? <VaultSetup onCreated={unlock} /> : !unlocked ? <VaultUnlock vault={vault} onUnlocked={unlock} /> : <Dashboard />}
      <VaultHelp />
    </div>
  );
}
